const EMBED_MESSAGE_SOURCE = "twelve";
const EMBED_MESSAGE_VERSION = 1;

type EmbedMessage = { event: string } & Record<string, unknown>;

/**
 * Posts a namespaced message to the embedding parent page, restricted to the
 * origins whitelisted for this location/account (`EmbedAllowedOrigin`). No-ops
 * when the form is loaded standalone (not in an iframe) or when no origins are
 * configured. Every payload is wrapped as `{ source: "twelve", v: 1, ... }` so
 * parent listeners can filter reliably against other postMessage traffic.
 *
 * This is a stable integration contract: keep `source`/`v` and event names fixed
 * so a single parent-page listener works for every embedded location. Bump `v`
 * (don't rename per-tenant) if the shape ever changes.
 *
 * IMPORTANT: keep payloads PII/PHI-free. The parent page forwards these to its
 * own analytics / ad vendors (GTM, GA4, Meta).
 */
export function postToEmbedParents(origins: string[], message: EmbedMessage) {
  if (typeof window === "undefined" || window.parent === window) return;

  const payload = { source: EMBED_MESSAGE_SOURCE, v: EMBED_MESSAGE_VERSION, ...message };
  for (const origin of resolveEmbedTargetOrigins(origins, embedParentOrigin())) {
    try {
      window.parent.postMessage(payload, origin);
    } catch {
      /* invalid target origin or blocked post */
    }
  }
}

/**
 * Whether a message posted now would reach the embedding parent: the form is framed, and the
 * parent's origin is known and allowed. Exact entries post blindly when the origin is unknown, so
 * "posted" alone doesn't mean "received".
 */
export function embedParentReceives(origins: string[]): boolean {
  if (typeof window === "undefined" || window.parent === window) return false;
  return embedParentAllowed(origins, embedParentOrigin());
}

/** Exported for tests. */
export function embedParentAllowed(origins: string[], parentOrigin: string | null): boolean {
  return (
    parentOrigin !== null && resolveEmbedTargetOrigins(origins, parentOrigin).includes(parentOrigin)
  );
}

/**
 * postMessage rejects wildcard target origins, so wildcard allowlist entries
 * (https://*.example.com) are matched against the actual embedding parent's
 * origin and resolve to that concrete origin. Exact entries post as-is — the
 * browser drops any whose origin doesn't match the parent. Exported for tests.
 */
export function resolveEmbedTargetOrigins(
  origins: string[],
  parentOrigin: string | null,
): string[] {
  const exact = origins.filter((origin) => !origin.includes("*"));
  const wildcards = origins.filter((origin) => origin.includes("*"));

  const matched =
    parentOrigin && wildcards.some((pattern) => matchesWildcardOrigin(pattern, parentOrigin));
  return matched ? [...new Set([...exact, parentOrigin])] : exact;
}

// CSP frame-ancestors semantics: same scheme and port, and the host must be a
// subdomain of the pattern (the bare domain itself does not match).
function matchesWildcardOrigin(pattern: string, origin: string): boolean {
  const [, scheme, remainder] = pattern.match(/^(https?):\/\/\*\.([^/]+)$/) ?? [];
  if (!scheme || !remainder) return false;

  try {
    const url = new URL(origin);
    const host = url.port ? `${url.hostname}:${url.port}` : url.hostname;
    return url.protocol === `${scheme}:` && host.endsWith(`.${remainder.toLowerCase()}`);
  } catch {
    return false;
  }
}

// Best-effort: ancestorOrigins is missing in Firefox, and referrer can be
// suppressed by the host page's referrer policy. When neither is available,
// wildcard entries simply don't deliver (same silent no-op as a non-matching
// exact origin).
function embedParentOrigin(): string | null {
  // The lib.dom type is non-optional, but Firefox doesn't implement ancestorOrigins.
  const ancestor =
    "ancestorOrigins" in window.location ? window.location.ancestorOrigins.item(0) : null;
  if (ancestor) return ancestor;

  if (document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      return null;
    }
  }
  return null;
}
